"use client";

import type { OrgFetchForOrg } from "@workspace/ui/org-fetch";
import {
  captureOrgAuthTelemetry,
  type OrgAuthRecoveryTrigger,
  type OrgAuthTelemetryCapture,
  OrgAuthTelemetryEvent,
  orgAuthRouteLabelForInput,
  safeOrgAuthTelemetryCode,
} from "./telemetry.org-auth";

type GetOrgToken = (options: {
  organizationId: string;
  skipCache?: boolean;
}) => Promise<string | null>;
type FetchImplementation = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type GetBrowserOrigin = () => string;

const SAFE_ERROR_CODE = /^[a-z0-9][a-z0-9_.:-]*$/i;

function defaultOrgFetchMessage(status: number): string {
  if (status === 401) return "Authentication is required. Please sign in again.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "The requested resource was not found.";
  if (status >= 500) return "The server could not complete the request. Please try again.";
  return "The request could not be completed. Please try again.";
}

export class OrgFetchError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly apiMessage?: string,
  ) {
    super(apiMessage ?? defaultOrgFetchMessage(status));
    this.name = "OrgFetchError";
  }
}

function requestHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  const headers =
    typeof Request !== "undefined" && input instanceof Request
      ? new Headers(input.headers)
      : new Headers();
  new Headers(init?.headers).forEach((value, name) => {
    headers.set(name, value);
  });
  return headers;
}

function inputOrigin(input: RequestInfo | URL, browserOrigin: string): string {
  const value = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  return new URL(value, browserOrigin).origin;
}

function retryInput(input: RequestInfo | URL): RequestInfo | URL {
  return typeof Request !== "undefined" && input instanceof Request ? input.clone() : input;
}

async function replayableRequestInit(init?: RequestInit): Promise<RequestInit | undefined> {
  if (
    !init?.body ||
    typeof ReadableStream === "undefined" ||
    !(init.body instanceof ReadableStream)
  ) {
    return init;
  }

  // RequestInit can override a Request's body with a one-shot stream. Buffer
  // that uncommon case once so the authorized replay sends the same bytes.
  const body = new Uint8Array(await new Response(init.body).arrayBuffer());
  return { ...init, body };
}

async function responseError(response: Response): Promise<OrgFetchError> {
  const payload = (await response.json().catch(() => null)) as {
    code?: unknown;
    error?: unknown;
    message?: unknown;
  } | null;
  const rawError = typeof payload?.error === "string" ? payload.error : null;
  const candidate = typeof payload?.code === "string" ? payload.code : rawError;
  const code = candidate && SAFE_ERROR_CODE.test(candidate) ? candidate : `http_${response.status}`;
  const publicMessage = safeApiMessage(
    typeof payload?.message === "string"
      ? payload.message
      : rawError && !SAFE_ERROR_CODE.test(rawError)
        ? rawError
        : null,
  );
  return new OrgFetchError(response.status, code, publicMessage);
}

function safeApiMessage(value: string | null): string | undefined {
  if (!value) return undefined;
  const normalized = Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint < 32 || codePoint === 127 ? " " : character;
  })
    .join("")
    .trim();
  return normalized ? normalized.slice(0, 1_000) : undefined;
}

export function createOrgFetch(
  getToken: GetOrgToken,
  fetchImplementation: FetchImplementation = (input, init) => fetch(input, init),
  getBrowserOrigin: GetBrowserOrigin = () =>
    typeof window === "undefined" ? "http://localhost" : window.location.origin,
  captureTelemetry?: OrgAuthTelemetryCapture,
): OrgFetchForOrg {
  return async (orgId, input, init) => {
    const browserOrigin = getBrowserOrigin();
    const route = orgAuthRouteLabelForInput(input, browserOrigin);
    if (inputOrigin(input, browserOrigin) !== browserOrigin) {
      const error = new OrgFetchError(400, "cross_origin_request_blocked");
      captureFinalFailure(captureTelemetry, route, error);
      throw error;
    }

    // Clone a Request before the first fetch consumes its body so the one
    // authorized replay remains possible. String/URL inputs are reusable.
    const replayInput = retryInput(input);
    const token = await getToken({ organizationId: orgId });
    if (!token) {
      return refreshAndRetry({
        captureTelemetry,
        fetchImplementation,
        getToken,
        init,
        initialError: new OrgFetchError(401, "clerk_token_unavailable"),
        input: replayInput,
        orgId,
        route,
        trigger: "token_unavailable",
      });
    }

    const requestInit = await replayableRequestInit(init);
    const response = await fetchWithToken(fetchImplementation, token, input, requestInit);
    if (response.ok) return response;

    const error = await responseError(response);
    if (error.status === 401 && error.code === "unauthenticated") {
      return refreshAndRetry({
        captureTelemetry,
        fetchImplementation,
        getToken,
        init: requestInit,
        initialError: error,
        input: replayInput,
        orgId,
        route,
        trigger: "session_bearer_unauthenticated",
      });
    }

    captureFinalFailure(captureTelemetry, route, error);
    throw error;
  };
}

async function fetchWithToken(
  fetchImplementation: FetchImplementation,
  token: string,
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = requestHeaders(input, init);
  headers.set("Authorization", `Bearer ${token}`);
  return fetchImplementation(input, { ...init, headers });
}

async function refreshAndRetry(args: {
  captureTelemetry: OrgAuthTelemetryCapture | undefined;
  fetchImplementation: FetchImplementation;
  getToken: GetOrgToken;
  init: RequestInit | undefined;
  initialError: OrgFetchError;
  input: RequestInfo | URL;
  orgId: string;
  route: string;
  trigger: OrgAuthRecoveryTrigger;
}): Promise<Response> {
  let token: string | null;
  try {
    token = await args.getToken({ organizationId: args.orgId, skipCache: true });
  } catch {
    const error = new OrgFetchError(401, "clerk_token_refresh_failed");
    captureRecovery(args, "failed", error);
    throw error;
  }

  if (!token) {
    const error = new OrgFetchError(401, "clerk_token_unavailable");
    captureRecovery(args, "failed", error);
    throw error;
  }

  let response: Response;
  try {
    response = await fetchWithToken(args.fetchImplementation, token, args.input, args.init);
  } catch (error) {
    captureOrgAuthTelemetry(args.captureTelemetry, OrgAuthTelemetryEvent.RECOVERY, {
      route: args.route,
      trigger: args.trigger,
      outcome: "failed",
      initial_status: args.initialError.status,
      initial_code: safeOrgAuthTelemetryCode(args.initialError.code),
      final_status: 0,
      final_code: "network_error",
    });
    throw error;
  }

  if (response.ok) {
    captureOrgAuthTelemetry(args.captureTelemetry, OrgAuthTelemetryEvent.RECOVERY, {
      route: args.route,
      trigger: args.trigger,
      outcome: "recovered",
      initial_status: args.initialError.status,
      initial_code: safeOrgAuthTelemetryCode(args.initialError.code),
      final_status: response.status,
      final_code: "ok",
    });
    return response;
  }

  const error = await responseError(response);
  captureRecovery(args, "failed", error);
  throw error;
}

function captureRecovery(
  args: {
    captureTelemetry: OrgAuthTelemetryCapture | undefined;
    initialError: OrgFetchError;
    route: string;
    trigger: OrgAuthRecoveryTrigger;
  },
  outcome: "failed",
  finalError: OrgFetchError,
): void {
  captureOrgAuthTelemetry(args.captureTelemetry, OrgAuthTelemetryEvent.RECOVERY, {
    route: args.route,
    trigger: args.trigger,
    outcome,
    initial_status: args.initialError.status,
    initial_code: safeOrgAuthTelemetryCode(args.initialError.code),
    final_status: finalError.status,
    final_code: safeOrgAuthTelemetryCode(finalError.code),
  });
}

function captureFinalFailure(
  captureTelemetry: OrgAuthTelemetryCapture | undefined,
  route: string,
  error: OrgFetchError,
): void {
  captureOrgAuthTelemetry(captureTelemetry, OrgAuthTelemetryEvent.FINAL_FAILURE, {
    route,
    final_status: error.status,
    final_code: safeOrgAuthTelemetryCode(error.code),
  });
}
